import { useCallback, useEffect, useState } from 'react';
import { Clock, Loader2, Minus, Plus } from 'lucide-react';
import {
  SERVICE_HOUR_RATE_LABEL,
  SERVICE_HOUR_RATE_USD,
  fetchServiceHoursSnapshot,
  formatHours,
  purchaseServiceHours,
  setServiceHourRetainer,
  type ServiceHoursSnapshot,
} from '../lib/serviceHours';

type Props = {
  businessId: string;
  isBusinessOwner: boolean;
};

export default function MyAppsServiceHoursCard({ businessId, isBusinessOwner }: Props) {
  const [snapshot, setSnapshot] = useState<ServiceHoursSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [buyHours, setBuyHours] = useState(5);
  const [retainerQty, setRetainerQty] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const snap = await fetchServiceHoursSnapshot(businessId);
      setSnapshot(snap);
      setRetainerQty(snap.retainer.quantity || 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load service hours');
      setSnapshot(null);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    void load();
  }, [load]);

  const onBuy = async () => {
    if (!isBusinessOwner) return;
    setBusy(true);
    setError(null);
    try {
      const origin = window.location.origin;
      const res = await purchaseServiceHours({
        businessId,
        hours: buyHours,
        successUrl: `${origin}/my-apps?service_hours=success`,
        cancelUrl: `${origin}/my-apps?service_hours=cancel`,
      });
      if (res.checkoutUrl) {
        window.location.href = res.checkoutUrl;
        return;
      }
      setError('No checkout URL returned');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Purchase failed');
    } finally {
      setBusy(false);
    }
  };

  const onSaveRetainer = async () => {
    if (!isBusinessOwner) return;
    setBusy(true);
    setError(null);
    try {
      await setServiceHourRetainer({ businessId, quantity: retainerQty });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update retainer');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="p-5 md:p-6 space-y-5">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center shadow-sm flex-shrink-0">
            <Clock className="h-6 w-6 text-white" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-gray-900">Professional services</h2>
            <p className="text-sm text-gray-500 mt-1 leading-snug">
              Setup, SEO, and consulting hours at {SERVICE_HOUR_RATE_LABEL}. Unused hours roll over.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading balance…
          </div>
        ) : (
          <>
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2">
                {error}
              </div>
            )}

            <div className="flex flex-wrap gap-6">
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-400 font-medium">Balance</p>
                <p className="text-2xl font-bold text-gray-900 tabular-nums">
                  {formatHours(snapshot?.balance.hours_balance ?? 0)}
                  <span className="text-sm font-normal text-gray-500"> hrs</span>
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-400 font-medium">Retainer</p>
                <p className="text-lg font-semibold text-gray-900 tabular-nums">
                  {snapshot?.retainer.quantity ?? 0} hrs/mo
                  {(snapshot?.retainer.monthly_usd ?? 0) > 0 && (
                    <span className="text-sm font-normal text-gray-500">
                      {' '}
                      · ${snapshot?.retainer.monthly_usd}/mo
                    </span>
                  )}
                </p>
              </div>
            </div>

            {isBusinessOwner && (
              <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-gray-100">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-900">Buy hours</p>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className="p-2 rounded-lg border border-gray-200"
                      onClick={() => setBuyHours((h) => Math.max(1, h - 1))}
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="flex-1 text-center text-sm tabular-nums font-medium">
                      {buyHours} hrs · ${buyHours * SERVICE_HOUR_RATE_USD}
                    </span>
                    <button
                      type="button"
                      className="p-2 rounded-lg border border-gray-200"
                      onClick={() => setBuyHours((h) => h + 1)}
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void onBuy()}
                    className="w-full py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-black disabled:opacity-50"
                  >
                    {busy ? 'Starting…' : 'Checkout'}
                  </button>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-900">Monthly retainer</p>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className="p-2 rounded-lg border border-gray-200"
                      onClick={() => setRetainerQty((q) => Math.max(0, q - 1))}
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="flex-1 text-center text-sm tabular-nums font-medium">
                      {retainerQty === 0
                        ? 'Off'
                        : `${retainerQty} hrs/mo · $${retainerQty * SERVICE_HOUR_RATE_USD}`}
                    </span>
                    <button
                      type="button"
                      className="p-2 rounded-lg border border-gray-200"
                      onClick={() => setRetainerQty((q) => q + 1)}
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void onSaveRetainer()}
                    className="w-full py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Save retainer
                  </button>
                </div>
              </div>
            )}

            {!isBusinessOwner && (
              <p className="text-xs text-gray-500">Only the business owner can buy hours or change the retainer.</p>
            )}

            <div>
              <button
                type="button"
                onClick={() => setHistoryOpen((v) => !v)}
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                {historyOpen ? 'Hide history' : 'View history'}
              </button>
              {historyOpen && (
                <div className="mt-3 space-y-2 max-h-56 overflow-y-auto">
                  {(snapshot?.transactions || []).length === 0 && (
                    <p className="text-sm text-gray-500">No transactions yet.</p>
                  )}
                  {(snapshot?.transactions || []).map((tx) => (
                    <div
                      key={tx.id}
                      className="flex justify-between gap-3 text-sm py-1.5 border-b border-gray-50 last:border-0"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-gray-800 capitalize">
                          {String(tx.transaction_type).replace('_', ' ')}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{tx.note || '—'}</p>
                      </div>
                      <p
                        className={`tabular-nums font-semibold shrink-0 ${
                          tx.hours >= 0 ? 'text-emerald-700' : 'text-red-700'
                        }`}
                      >
                        {tx.hours >= 0 ? '+' : ''}
                        {formatHours(tx.hours)}h
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Monthly retainer estimate for My Apps totals (0 if inactive). */
export function serviceHourRetainerMonthlyFromSnapshot(
  snap: ServiceHoursSnapshot | null | undefined,
): number {
  if (!snap?.retainer || snap.retainer.quantity < 1) return 0;
  return snap.retainer.monthly_usd || snap.retainer.quantity * SERVICE_HOUR_RATE_USD;
}
